import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Area,
  AreaChart,
} from "recharts";
import {
  Activity,
  Brain,
  Cpu,
  Database,
  Settings,
  ArrowLeft,
  Key,
  LogOut,
  CheckCircle,
  ShieldAlert,
  Lock,
  Globe,
  Plug,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import APIKeysView from "../components/APIKeysView";
import ConnectorsView from "../components/ConnectorsView";
import MemoryGraph from "../components/MemoryGraph";
import { confidenceSeries, initialNodes, initialLinks } from "../lib/mockData";

// ── Sidebar ──────────────────────────────────────────────────────────────────
function Sidebar({ active, setActive, agents, stats, onLogout }) {
  const nav = [
    { id: "overview", label: "Overview", icon: Activity },
    { id: "agents", label: "Agents", icon: Cpu },
    { id: "graph", label: "Knowledge Graph", icon: Brain },
    { id: "stream", label: "Event stream", icon: Database },
    { id: "connectors", label: "Connectors", icon: Plug },
    { id: "api-keys", label: "API Keys", icon: Key },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <aside className="w-60 shrink-0 border-r border-line-1 bg-surface min-h-[calc(100vh-3.5rem)] sticky top-14 flex flex-col">
      <div className="p-4 border-b border-line-1">
        <Link
          to="/"
          className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-ink-500 hover:text-cyber"
        >
          <ArrowLeft size={12} /> back to site
        </Link>
      </div>

      <div className="p-3 flex-1">
        <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500 px-3 py-2 truncate">
          Tenant · {stats?.tenant_name || "…"}
        </div>
        <nav className="space-y-0.5">
          {nav.map((n) => (
            <button
              key={n.id}
              onClick={() => setActive(n.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-[12px] tracking-[0.15em] uppercase border-l-2 transition-colors ${
                active === n.id
                  ? "border-cyber text-cyber bg-cyber/5"
                  : "border-transparent text-ink-300 hover:text-ink-100 hover:border-line-2"
              }`}
            >
              <n.icon size={14} strokeWidth={1.5} /> {n.label}
            </button>
          ))}
        </nav>

        {agents.length > 0 && (
          <div className="mt-8 px-3">
            <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500 mb-2">
              Active agents
            </div>
            <ul className="space-y-1.5">
              {agents.slice(0, 5).map((a) => (
                <li
                  key={a.id}
                  className="flex items-center justify-between text-[11px] text-ink-300"
                >
                  <span className="flex items-center gap-2 truncate">
                    <span className="w-1.5 h-1.5 bg-terminal shrink-0" />
                    <span className="truncate">{a.name}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="p-3 border-t border-line-1">
        <div className="px-3 text-[10px] tracking-[0.2em] uppercase text-ink-500 truncate mb-2">
          {stats?.email}
        </div>
        <span
          className={`mx-3 text-[10px] px-2 py-0.5 uppercase tracking-widest ${
            stats?.plan === "pro"
              ? "bg-brand/20 text-brand"
              : "bg-line-1 text-ink-400"
          }`}
        >
          {stats?.plan || "free"}
        </span>
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 mt-2 text-[12px] tracking-[0.15em] uppercase border-l-2 border-transparent text-ink-500 hover:text-hotpink hover:border-hotpink transition-colors"
        >
          <LogOut size={14} strokeWidth={1.5} /> Log out
        </button>
      </div>
    </aside>
  );
}

// ── Metric tile ──────────────────────────────────────────────────────────────
function MetricTile({ label, value, delta, deltaTone = "cyan", icon: Icon, sparkline }) {
  const toneCls =
    deltaTone === "cyan"
      ? "text-cyber"
      : deltaTone === "amber"
        ? "text-amber"
        : "text-hotpink";

  const gradId = `sp-${label.replace(/\s+/g, "-")}`;

  return (
    <div className="border border-line-1 bg-surface p-5 relative group hover:border-line-2 transition-colors">
      <div className="flex items-start justify-between">
        <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500">{label}</div>
        <Icon size={14} className="text-ink-500 group-hover:text-cyber" strokeWidth={1.5} />
      </div>
      <div className="mt-4 flex items-baseline gap-2">
        <div className="font-display font-black text-3xl text-ink-100">{value}</div>
        <div className={`text-[11px] tracking-[0.2em] uppercase ${toneCls}`}>{delta}</div>
      </div>
      {sparkline && (
        <div className="h-10 mt-3 -mx-1" style={{ minWidth: 0 }}>
          <ResponsiveContainer width="99%" height="100%">
            <AreaChart data={sparkline} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#00E5FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="v"
                stroke="#00E5FF"
                strokeWidth={1.4}
                fill={`url(#${gradId})`}
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// ── Confidence chart ─────────────────────────────────────────────────────────
function ConfidenceChart() {
  return (
    <div className="border border-line-1 bg-surface p-5 col-span-1 md:col-span-2">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500">
            Confidence dynamics · 30-run trajectory
          </div>
          <div className="font-display font-bold text-lg mt-1">
            mnemo-trained <span className="text-cyber">vs</span> stateless baseline
          </div>
        </div>
        <div className="flex gap-3 text-[10px] tracking-[0.3em] uppercase">
          <span className="flex items-center gap-1.5 text-cyber">
            <span className="w-3 h-px bg-cyber inline-block" /> mnemo
          </span>
          <span className="flex items-center gap-1.5 text-ink-500">
            <span className="w-3 h-px bg-ink-500 inline-block" /> baseline
          </span>
        </div>
      </div>
      <div className="h-56 -ml-2">
        <ResponsiveContainer>
          <LineChart data={confidenceSeries}>
            <CartesianGrid stroke="#1A1A24" strokeDasharray="2 4" vertical={false} />
            <XAxis
              dataKey="run"
              stroke="#8A8A98"
              fontSize={10}
              tickLine={false}
              axisLine={{ stroke: "#1A1A24" }}
            />
            <YAxis
              stroke="#8A8A98"
              fontSize={10}
              domain={[0, 1]}
              tickLine={false}
              axisLine={{ stroke: "#1A1A24" }}
            />
            <Tooltip
              contentStyle={{
                background: "#0C0C10",
                border: "1px solid #1A1A24",
                borderRadius: 0,
                fontFamily: "IBM Plex Mono",
                fontSize: 11,
              }}
              labelStyle={{ color: "#8A8A98", textTransform: "uppercase", letterSpacing: "0.2em" }}
            />
            <Line
              type="monotone"
              dataKey="baseline"
              stroke="#8A8A98"
              strokeWidth={1.2}
              dot={false}
              strokeDasharray="3 3"
            />
            <Line
              type="monotone"
              dataKey="mnemo"
              stroke="#00E5FF"
              strokeWidth={2}
              dot={{ fill: "#FFAB00", stroke: "#FFAB00", r: 2 }}
              activeDot={{ r: 4, fill: "#00E5FF" }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ── Event stream ─────────────────────────────────────────────────────────────
const EVENT_TONE = { capture: "green", reflect: "cyan", recall: "cyan", warn: "pink", error: "pink" };

function formatRawEvent(e) {
  const kind = (e.event_type || "event").toUpperCase().slice(0, 7);
  const tone = EVENT_TONE[e.event_type?.toLowerCase()] || "cyan";
  const ts = new Date(e.created_at);
  const t = `${ts.getHours().toString().padStart(2, "0")}:${ts.getMinutes().toString().padStart(2, "0")}:${ts.getSeconds().toString().padStart(2, "0")}`;
  const meta = e.metadata || {};
  const text =
    meta.task ||
    meta.description ||
    meta.message ||
    `agent=${e.agent_id?.slice(-6) || "?"} · ${e.event_type}`;
  return { t, kind, tone, text };
}

function EventStream({ events, loading }) {
  const tone = (t) =>
    t === "cyan"
      ? "text-cyber"
      : t === "amber"
        ? "text-amber"
        : t === "pink"
          ? "text-hotpink"
          : "text-terminal";

  return (
    <div className="border border-line-1 bg-surface p-5 flex flex-col min-h-[340px]">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500">
            Live · event stream
          </div>
          <div className="font-display font-bold text-lg mt-1">tail -f reflection.log</div>
        </div>
        <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-cyber">
          <span className="w-1.5 h-1.5 bg-cyber animate-pulse" /> live
        </div>
      </div>
      <div className="flex-1 overflow-hidden border border-line-1 bg-void/60 p-3 font-mono text-[12px] leading-6">
        {loading ? (
          <div className="text-ink-500 animate-pulse">fetching events…</div>
        ) : events.length === 0 ? (
          <div className="text-ink-500">
            no events yet · start capturing to see activity
          </div>
        ) : (
          <div className="space-y-1">
            {events.map((e, i) => (
              <div key={i} className="flex gap-3">
                <span className="text-ink-500 shrink-0">[{e.t}]</span>
                <span className={`${tone(e.tone)} shrink-0 w-20`}>{e.kind}</span>
                <span className="text-ink-300 truncate">{e.text}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Lesson health panel ──────────────────────────────────────────────────────
function HealthPanel({ health, gapCount, resolutionRate }) {
  const rows = [
    { label: "Healthy", value: health?.healthy ?? 0, color: "text-terminal" },
    { label: "Building confidence", value: health?.low_confidence ?? 0, color: "text-cyber" },
    { label: "Needs validation", value: health?.needs_validation ?? 0, color: "text-amber" },
    { label: "Low confidence", value: health?.critical ?? 0, color: "text-hotpink" },
    { label: "Knowledge gaps", value: gapCount ?? 0, color: "text-amber" },
  ];

  return (
    <div className="border border-line-1 bg-surface p-5">
      <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500 mb-1">
        lesson.health
      </div>
      <div className="font-display font-bold text-lg mb-4">
        avg conf{" "}
        <span className="text-cyber">{health?.avg_confidence ?? "—"}</span>
      </div>
      <ul className="space-y-2.5">
        {rows.map((r) => (
          <li key={r.label} className="flex justify-between items-center text-xs">
            <span className="text-ink-400">{r.label}</span>
            <span className={`font-mono font-bold ${r.color}`}>{r.value}</span>
          </li>
        ))}
        {resolutionRate != null && (
          <li className="flex justify-between items-center text-xs pt-2 border-t border-line-1">
            <span className="text-ink-400">Resolution rate (30d)</span>
            <span className="font-mono font-bold text-cyber">
              {Math.round(resolutionRate * 100)}%
            </span>
          </li>
        )}
      </ul>
    </div>
  );
}

// ── Overview panel ───────────────────────────────────────────────────────────
function OverviewPanel({ stats, rawEvents, eventsLoading, activity }) {
  const activitySparkline = useMemo(() => {
    if (activity.length > 0) {
      const max = Math.max(...activity.map((d) => d.events), 1);
      return activity.map((d) => ({ v: d.events / max }));
    }
    return Array.from({ length: 7 }, (_, i) => ({ v: 0.2 + Math.sin(i) * 0.15 }));
  }, [activity]);

  const lessonSparkline = useMemo(
    () =>
      Array.from({ length: 14 }, (_, i) => ({
        v: 0.3 + (i / 14) * 0.5 + Math.sin(i * 0.7) * 0.08,
      })),
    [],
  );

  const confSparkline = useMemo(() => {
    const base = stats?.lesson_health?.avg_confidence ?? 0.5;
    return Array.from({ length: 14 }, (_, i) => ({
      v: Math.max(0, Math.min(1, base - 0.15 + (i / 14) * 0.15 + Math.sin(i * 0.8) * 0.04)),
    }));
  }, [stats]);

  const formattedEvents = useMemo(() => rawEvents.map(formatRawEvent), [rawEvents]);

  const subtitle = [
    stats?.agents_count > 0
      ? `${stats.agents_count} agent${stats.agents_count !== 1 ? "s" : ""} online`
      : null,
    stats?.lessons_count > 0 ? `${stats.lessons_count} lessons` : null,
    stats?.total_recalls > 0 ? `${stats.total_recalls} recalls` : null,
  ]
    .filter(Boolean)
    .join(" · ") || "no activity yet · start capturing to build memory";

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-cyber mb-2">
            / control.room / overview
          </div>
          <h1 className="font-display font-black uppercase text-3xl md:text-4xl tracking-tight">
            Welcome back, <span className="text-cyber">operator</span>.
          </h1>
          <p className="text-ink-500 text-sm mt-2">{subtitle}</p>
        </div>
        <div className="hidden md:flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-ink-500">
          <span>tenant:</span>
          <code className="text-cyber">{stats?.tenant_name || "—"}</code>
        </div>
      </div>

      {/* Metric tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricTile
          label="Lessons captured"
          value={stats?.lessons_count ?? 0}
          delta={`${stats?.total_reflects ?? 0} reflected`}
          icon={Brain}
          sparkline={lessonSparkline}
        />
        <MetricTile
          label="Active agents"
          value={stats?.agents_count ?? 0}
          delta={`${stats?.api_keys_count ?? 0} api keys`}
          deltaTone="amber"
          icon={Cpu}
          sparkline={activitySparkline}
        />
        <MetricTile
          label="Total recalls"
          value={stats?.total_recalls ?? 0}
          delta="lifetime"
          icon={Database}
          sparkline={activitySparkline}
        />
        <MetricTile
          label="Avg confidence"
          value={stats?.lesson_health?.avg_confidence ?? "—"}
          delta={
            (stats?.lesson_health?.healthy ?? 0) > 0
              ? `${stats.lesson_health.healthy} healthy`
              : "building…"
          }
          deltaTone={(stats?.lesson_health?.healthy ?? 0) > 0 ? "cyan" : "amber"}
          icon={Brain}
          sparkline={confSparkline}
        />
      </div>

      {/* Chart + Stream */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ConfidenceChart />
        <EventStream events={formattedEvents} loading={eventsLoading} />
      </div>

      {/* Activity chart + Health */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-line-1 bg-surface p-5">
          <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500 mb-1">
            activity · 7d
          </div>
          <div className="font-display font-bold text-lg mb-4">events per day</div>
          <div className="h-44">
            <ResponsiveContainer>
              <AreaChart
                data={
                  activity.length
                    ? activity
                    : Array.from({ length: 7 }, (_, i) => ({ date: `Day ${i + 1}`, events: 0 }))
                }
                margin={{ top: 4, right: 4, bottom: 0, left: -20 }}
              >
                <defs>
                  <linearGradient id="actGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#00E5FF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#1A1A24" strokeDasharray="2 4" vertical={false} />
                <XAxis
                  dataKey="date"
                  stroke="#8A8A98"
                  fontSize={10}
                  tickLine={false}
                  axisLine={{ stroke: "#1A1A24" }}
                />
                <YAxis
                  stroke="#8A8A98"
                  fontSize={10}
                  tickLine={false}
                  axisLine={{ stroke: "#1A1A24" }}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    background: "#0C0C10",
                    border: "1px solid #1A1A24",
                    borderRadius: 0,
                    fontFamily: "IBM Plex Mono",
                    fontSize: 11,
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="events"
                  stroke="#00E5FF"
                  strokeWidth={1.5}
                  fill="url(#actGrad)"
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <HealthPanel
          health={stats?.lesson_health}
          gapCount={stats?.knowledge_gap_count}
          resolutionRate={stats?.resolution_rate_30d}
        />
      </div>

      {/* Getting started if empty */}
      {(stats?.lessons_count ?? 0) === 0 && (
        <div className="border border-line-1 bg-surface p-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500 mb-1">
            getting.started
          </div>
          <div className="font-display font-bold text-lg mb-5">
            3 steps to cure AI amnesia
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                n: "01",
                title: "Create an API Key",
                body: "Go to API Keys in the sidebar and generate your first key.",
              },
              {
                n: "02",
                title: "Install the SDK",
                code: "pip install mnemonic-sdk",
              },
              {
                n: "03",
                title: "Start capturing",
                body: "Wrap your agent sessions. Lessons appear here automatically.",
              },
            ].map((s) => (
              <div key={s.n} className="border border-line-1 p-4">
                <div className="font-mono text-[10px] text-cyber mb-2">{s.n}</div>
                <div className="font-semibold text-ink-100 text-sm mb-1">{s.title}</div>
                {s.code ? (
                  <div className="bg-void border border-line-2 px-3 py-2 font-mono text-xs text-terminal mt-2">
                    {s.code}
                  </div>
                ) : (
                  <div className="text-xs text-ink-500">{s.body}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Agents panel ─────────────────────────────────────────────────────────────
function AgentsPanel({ agents }) {
  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-cyber mb-2">
          / control.room / agents
        </div>
        <h1 className="font-display font-black uppercase text-3xl tracking-tight">
          Agent <span className="text-cyber">Registry</span>
        </h1>
        <p className="text-ink-500 text-sm mt-2">
          {agents.length} agent{agents.length !== 1 ? "s" : ""} registered
        </p>
      </div>
      {agents.length === 0 ? (
        <div className="border border-line-1 bg-surface p-12 text-center">
          <Cpu size={40} className="mx-auto mb-4 text-ink-500" strokeWidth={1.5} />
          <div className="text-ink-400 text-sm">No agents registered yet.</div>
          <div className="text-ink-500 text-xs mt-2">
            Agents appear here automatically when they first call recall or capture.
          </div>
        </div>
      ) : (
        <div className="border border-line-1 bg-surface">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] tracking-[0.3em] uppercase text-ink-500 border-b border-line-1">
                <th className="text-left px-5 py-3 font-normal">id</th>
                <th className="text-left px-5 py-3 font-normal">name</th>
                <th className="text-left px-5 py-3 font-normal">registered</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => (
                <tr
                  key={a.id}
                  className="border-b border-line-1 hover:bg-cyber/5 transition-colors"
                >
                  <td className="px-5 py-3 font-mono text-xs text-ink-500">{a.id.slice(-8)}</td>
                  <td className="px-5 py-3 text-ink-100">
                    <span className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-terminal shrink-0" />
                      {a.name}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-ink-400 text-xs">
                    {new Date(a.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Knowledge graph panel ─────────────────────────────────────────────────────
function useGraphData(scope) {
  const [nodes, setNodes] = useState(() => initialNodes.map((n) => ({ ...n })));
  const [links, setLinks] = useState(() => initialLinks.map((l) => ({ ...l })));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const apiKey = localStorage.getItem("mnemo_api_key");
    if (!apiKey) { setLoading(false); return; }
    setLoading(true);
    fetch(`/api/v1/lessons/graph?scope=${scope}&limit=80`, {
      headers: { Authorization: `Bearer ${apiKey}` },
    })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (data?.nodes?.length) {
          const mapped = data.nodes.map((n) => ({
            ...n,
            group:
              n.group === "procedure" ? "procedure"
              : n.group === "task" ? "task"
              : "lesson",
          }));
          setNodes(mapped);
          setLinks(data.links || []);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [scope]);

  return { nodes, links, loading };
}

function KnowledgeGraphPanel({ stats }) {
  const [view, setView] = useState("global");
  const { nodes, links, loading } = useGraphData(view);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-cyber mb-2">
            / control.room / graph
          </div>
          <h1 className="font-display font-black uppercase text-3xl tracking-tight">
            Knowledge <span className="text-cyber">Graph</span>
          </h1>
          <p className="text-ink-500 text-sm mt-2">
            {view === "global"
              ? "Community-wide lessons — shared across all Mnemonic users"
              : "Your private lessons — visible only to your team"}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setView("global")}
            className={`flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.2em] uppercase border transition-colors ${
              view === "global"
                ? "border-cyber text-cyber bg-cyber/5"
                : "border-line-2 text-ink-400 hover:border-line-1 hover:text-ink-200"
            }`}
          >
            <Globe size={12} /> Global
          </button>
          <button
            onClick={() => setView("private")}
            className={`flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.2em] uppercase border transition-colors ${
              view === "private"
                ? "border-brand text-brand bg-brand/5"
                : "border-line-2 text-ink-400 hover:border-line-1 hover:text-ink-200"
            }`}
          >
            <Lock size={12} /> Private
          </button>
        </div>
      </div>

      <div className="border border-line-1 bg-surface relative vignette">
        <div className="flex items-center justify-between p-5 border-b border-line-1">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500">
              {view === "global" ? "global · shared knowledge pool" : "private · your tenant only"}
            </div>
            <div className="font-display font-bold text-lg mt-1">
              neural.memory.graph{" "}
              <span className={loading ? "text-amber" : view === "global" ? "text-cyber" : "text-brand"}>●</span>
            </div>
          </div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500">
            {loading ? "loading…" : `${nodes.length} nodes · ${links.length} edges`}
          </div>
        </div>
        <MemoryGraph nodes={nodes} links={links} height={560} compact />
      </div>
    </div>
  );
}

// ── (Lessons panel kept for internal use but removed from nav) ────────────────
function _LessonsPanel({ stats }) {
  const health = stats?.lesson_health;
  const total = stats?.lessons_count ?? 0;

  const bars = [
    { label: "Healthy (≥0.6)", value: health?.healthy ?? 0, color: "#3DF0B0", bg: "bg-terminal" },
    { label: "Building (0.35–0.6)", value: health?.low_confidence ?? 0, color: "#00E5FF", bg: "bg-cyber" },
    { label: "Needs validation", value: health?.needs_validation ?? 0, color: "#FFAB00", bg: "bg-amber" },
    { label: "Low (<0.35)", value: health?.critical ?? 0, color: "#FF2D78", bg: "bg-hotpink" },
    { label: "Deprecated", value: health?.deprecated ?? 0, color: "#3C3C50", bg: "bg-line-2" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-cyber mb-2">
          / control.room / lessons
        </div>
        <h1 className="font-display font-black uppercase text-3xl tracking-tight">
          Lesson <span className="text-cyber">Library</span>
        </h1>
        <p className="text-ink-500 text-sm mt-2">{total} lessons in your knowledge base</p>
      </div>

      {total === 0 ? (
        <div className="border border-line-1 bg-surface p-12 text-center">
          <Layers size={40} className="mx-auto mb-4 text-ink-500" strokeWidth={1.5} />
          <div className="text-ink-400 text-sm">No lessons yet.</div>
          <div className="text-ink-500 text-xs mt-2">
            Lessons are automatically extracted from agent sessions via reflection.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Health breakdown */}
          <div className="border border-line-1 bg-surface p-5">
            <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500 mb-1">
              health.breakdown
            </div>
            <div className="font-display font-bold text-lg mb-5">
              avg conf <span className="text-cyber">{health?.avg_confidence ?? "—"}</span>
            </div>
            <div className="space-y-3">
              {bars.map((b) => (
                <div key={b.label}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-ink-400">{b.label}</span>
                    <span className="font-mono text-ink-200">
                      {b.value}
                      {total > 0 && (
                        <span className="text-ink-500 ml-1">
                          ({Math.round((b.value / total) * 100)}%)
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="h-1 bg-line-1">
                    <div
                      className={`h-full ${b.bg}`}
                      style={{ width: `${total > 0 ? (b.value / total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="border border-line-1 bg-surface p-5 space-y-4">
            <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500 mb-1">
              knowledge.stats
            </div>
            <div className="font-display font-bold text-lg mb-4">memory overview</div>
            <ul className="space-y-3">
              {[
                { label: "Total lessons", value: total, color: "text-ink-100" },
                { label: "Total recalls", value: stats?.total_recalls ?? 0, color: "text-cyber" },
                { label: "Total reflects", value: stats?.total_reflects ?? 0, color: "text-terminal" },
                { label: "Knowledge gaps", value: stats?.knowledge_gap_count ?? 0, color: "text-amber" },
                {
                  label: "Resolution rate (30d)",
                  value:
                    stats?.resolution_rate_30d != null
                      ? `${Math.round(stats.resolution_rate_30d * 100)}%`
                      : "—",
                  color: "text-cyber",
                },
              ].map((r) => (
                <li key={r.label} className="flex justify-between items-center text-sm">
                  <span className="text-ink-400">{r.label}</span>
                  <span className={`font-mono font-bold ${r.color}`}>{r.value}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {stats?.knowledge_gap_count > 0 && (
        <div className="border border-amber/30 bg-amber/5 p-4 flex items-start gap-3">
          <ShieldAlert size={16} className="text-amber mt-0.5 shrink-0" />
          <div className="text-xs text-ink-300">
            <span className="text-amber font-semibold">{stats.knowledge_gap_count} knowledge gap{stats.knowledge_gap_count !== 1 ? "s" : ""} detected.</span>{" "}
            These are recurring task types where recall is returning no useful lessons.
            Capture more sessions in these domains to fill the gaps.
          </div>
        </div>
      )}
    </div>
  );
}

// ── Settings panel ───────────────────────────────────────────────────────────
function SettingsPanel({ stats, onLogout }) {
  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-cyber mb-2">
          / control.room / settings
        </div>
        <h1 className="font-display font-black uppercase text-3xl tracking-tight">
          Account <span className="text-cyber">Settings</span>
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-line-1 bg-surface p-6 space-y-4">
          <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500">Account</div>
          <ul className="space-y-3">
            {[
              { label: "Email", value: stats?.email || "—", cls: "text-ink-100 font-mono" },
              { label: "Tenant", value: stats?.tenant_name || "—", cls: "text-cyber font-mono" },
              {
                label: "Plan",
                value: stats?.plan || "free",
                cls:
                  stats?.plan === "pro"
                    ? "bg-brand/20 text-brand text-[10px] px-2 py-0.5 uppercase tracking-widest"
                    : "bg-line-1 text-ink-400 text-[10px] px-2 py-0.5 uppercase tracking-widest",
              },
            ].map((r) => (
              <li key={r.label} className="flex justify-between items-center text-sm">
                <span className="text-ink-400">{r.label}</span>
                <span className={r.cls}>{r.value}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="border border-line-1 bg-surface p-6 space-y-3">
          <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500">SDK Quick Start</div>
          <div className="bg-void border border-line-2 p-3 font-mono text-xs text-ink-300 space-y-1">
            <div className="text-ink-500"># Python</div>
            <div className="text-terminal">pip install mnemonic-sdk</div>
            <div className="mt-2 text-ink-500"># JavaScript / TypeScript</div>
            <div className="text-terminal">npm install @mnemonicai.official/sdk-js</div>
          </div>
          <div className="bg-void border border-line-2 p-3 font-mono text-xs text-ink-300 space-y-1">
            <div className="text-ink-500"># Claude Code hook</div>
            <div className="text-cyber">pip install mnemonic-sdk</div>
            <div className="text-cyber">mnemo install</div>
          </div>
        </div>
      </div>

      <div className="border border-line-1 bg-surface p-6 space-y-4">
        <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500">MCP Server — Claude Desktop / Cursor</div>
        <p className="text-xs text-ink-400">
          Install Mnemonic as an MCP server so Claude Desktop or Cursor can recall and capture lessons automatically — no code changes needed.
        </p>
        <div className="bg-void border border-line-2 p-3 font-mono text-xs text-ink-300 space-y-1">
          <div className="text-ink-500"># Install</div>
          <div className="text-terminal">npm install -g @mnemonicai.official/mcp</div>
        </div>
        <div className="bg-void border border-line-2 p-3 font-mono text-xs text-ink-300 space-y-1">
          <div className="text-ink-500">
            # Edit: %APPDATA%\Claude\claude_desktop_config.json
          </div>
          <div className="text-cyber whitespace-pre">{`{
  "mcpServers": {
    "mnemonic": {
      "command": "npx",
      "args": ["-y", "@mnemonicai.official/mcp"],
      "env": {
        "MNEMONIC_API_KEY": "${stats?.email ? "mnemo_sk_..." : "mnemo_sk_..."}",
        "MNEMONIC_AGENT_ID": "claude-desktop"
      }
    }
  }
}`}</div>
        </div>
      </div>

      <div className="border border-hotpink/30 bg-hotpink/5 p-4 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-ink-100">Log out</div>
          <div className="text-xs text-ink-500 mt-0.5">End your current session</div>
        </div>
        <button
          onClick={onLogout}
          className="text-[11px] tracking-[0.2em] uppercase border border-hotpink/50 text-hotpink px-4 py-2 hover:bg-hotpink/10 transition-colors"
        >
          Log out
        </button>
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { user, logout, apiFetch, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [active, setActive] = useState("overview");
  const [stats, setStats] = useState(null);
  const [agents, setAgents] = useState([]);
  const [activity, setActivity] = useState([]);
  const [rawEvents, setRawEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Wait for AuthContext to restore session from localStorage before redirecting
    if (authLoading) return;
    if (!user) {
      navigate("/login");
      return;
    }

    const safeJson = (r) => (r.ok ? r.json() : null);

    Promise.all([
      apiFetch("/dashboard/stats").then(safeJson),
      apiFetch("/dashboard/agents").then(safeJson),
      apiFetch("/dashboard/activity").then(safeJson),
    ])
      .then(([s, a, act]) => {
        setStats(s);
        setAgents(a || []);
        setActivity(act || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));

    const fetchEvents = () =>
      apiFetch("/dashboard/recent-events?limit=20")
        .then((r) => r.ok ? r.json() : null)
        .then((e) => {
          if (e) {
            setRawEvents(e);
            setEventsLoading(false);
          }
        })
        .catch(() => setEventsLoading(false));

    fetchEvents();
    const pollId = setInterval(fetchEvents, 15000);
    return () => clearInterval(pollId);
  }, [user, navigate, apiFetch, authLoading]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500 animate-pulse">
          loading control.room…
        </div>
      </div>
    );
  }

  return (
    <div className="App noise min-h-screen bg-void text-ink-100">
      {/* Top nav */}
      <div className="border-b border-line-1 bg-void/80 backdrop-blur sticky top-0 z-50">
        <div className="px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 border border-cyber/60 flex items-center justify-center">
              <Cpu size={14} className="text-cyber" strokeWidth={1.5} />
            </div>
            <span className="font-display font-black tracking-tight">
              MNEMO<span className="text-cyber">/</span>
              <span className="text-ink-500 text-xs">control.room</span>
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-ink-500">
            <span className="flex items-center gap-1.5 text-terminal">
              <CheckCircle size={12} /> system.ok
            </span>
            <span className="text-line-2">·</span>
            <span>{stats?.plan || "free"} plan</span>
          </div>
        </div>
      </div>

      <div className="flex">
        <Sidebar
          active={active}
          setActive={setActive}
          agents={agents}
          stats={stats}
          onLogout={logout}
        />

        <main className="flex-1 min-w-0 p-6 md:p-8 space-y-6">
          {active === "overview" && (
            <OverviewPanel
              stats={stats}
              rawEvents={rawEvents}
              eventsLoading={eventsLoading}
              activity={activity}
            />
          )}
          {active === "agents" && <AgentsPanel agents={agents} />}
          {active === "graph" && <KnowledgeGraphPanel stats={stats} />}
          {active === "stream" && (
            <div className="space-y-6">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-cyber mb-2">
                  / control.room / stream
                </div>
                <h1 className="font-display font-black uppercase text-3xl tracking-tight">
                  Event <span className="text-cyber">Stream</span>
                </h1>
                <p className="text-ink-500 text-sm mt-2">Live reflection and capture events</p>
              </div>
              <EventStream
                events={rawEvents.map(formatRawEvent)}
                loading={eventsLoading}
              />
            </div>
          )}
          {active === "connectors" && <ConnectorsView />}
          {active === "api-keys" && <APIKeysView />}
          {active === "settings" && <SettingsPanel stats={stats} onLogout={logout} />}
        </main>
      </div>
    </div>
  );
}
